import { configureMcpApiFetch } from '@api';
import { createContext, ReactNode, useContext, useLayoutEffect } from 'react';

/**
 * Host-injected API configuration for design-system **data providers**
 * (`AssetOverviewDataProvider`, etc.). Not part of the generative-UI JSON
 * contract — the LLM must never emit `basePath` or `token`.
 *
 * **Who wires it:** terminal wraps the generative-UI
 * render tree, e.g. around Alfred `Messages` / `Research`, with real values
 * from `config.api.root`, the auth store, and the host locale.
 *
 * **Who reads it:** mounting this provider calls `configureMcpApiFetch` so
 * `@api` helpers (`postEntity`, etc.) pick up `basePath` and `token` without
 * per-call threading. `useApiConfig()` remains available when a component
 * needs the values directly.
 */
export interface ApiConfig {
  basePath: string;
  token?: string;
  priceWsRoot?: string;
  delayedPriceWsRoot?: string;
  useDelayedPrices?: boolean;
  screenerExclusionEnabled?: boolean;
  dateLocale?: string;
}

const ApiConfigContext = createContext<ApiConfig>({ basePath: '/' });

export interface ApiConfigProviderProps extends Partial<ApiConfig> {
  children: ReactNode;
}

export const ApiConfigProvider = ({
  basePath = '/',
  token,
  priceWsRoot,
  delayedPriceWsRoot,
  useDelayedPrices = false,
  screenerExclusionEnabled = false,
  dateLocale,
  children,
}: ApiConfigProviderProps) => {
  useLayoutEffect(() => {
    configureMcpApiFetch({ basePath, token });
  }, [basePath, token]);

  return (
    <ApiConfigContext.Provider
      value={{
        basePath,
        token,
        priceWsRoot,
        delayedPriceWsRoot,
        useDelayedPrices,
        screenerExclusionEnabled,
        dateLocale,
      }}
    >
      {children}
    </ApiConfigContext.Provider>
  );
};

export const useApiConfig = (): ApiConfig => useContext(ApiConfigContext);
